import { useEffect, useRef, useState } from 'react';
import { SPEECH_RATE_VALUES, formatSpeechRateLabel } from '@orborix/voice-client';
import {
  CONV_REGION_ACCENT,
  ENGLISH_INTENSITIES,
  effectiveConvStyle,
  isConvStyleLocked,
  regionForAccent,
} from '@orborix/shared-types';
import { IconXSmall } from '../icons.js';
import { Switch } from './atoms.js';
import { CoachPicker } from './CoachPicker.js';
import {
  coachMutedFromMode,
  DENSITY_OPTIONS,
  REGION_OPTIONS,
  REGISTER_OPTIONS,
  SPANISH_VARIANT_OPTIONS,
  TONE_OPTIONS,
  type ConvSettings,
  type SpanishVariantId,
} from '../conversationTypes.js';
import type { UseCoachPickerReturn } from '../hooks/useCoachPicker.js';
import { sessionIntensity } from '../sessionIntensity.js';
import { LIVE_PA_AVAILABLE } from '../livePronunciation.js';
import { useSheetDrag } from '../../../hooks/useSheetDrag.js';
import { IntensityControl, AutoManualToggle } from '../../leveling/IntensityControl.js';
import { useEnglishLevel, type UseEnglishLevel } from '../../leveling/useEnglishLevel.js';
import { intensityById } from '../../leveling/levelData.js';
import { IconCheck, IconShield, IconWand } from '../../leveling/icons.js';
import '../../leveling/leveling.css';

/**
 * Ajustes de la charla (rediseño v10). Una sola puerta de entrada, reorganizada
 * por frecuencia real de cambio en CUATRO zonas — todo sigue editándose en vivo,
 * el modelo de datos NO cambia:
 *
 *   1. «Esta charla»      — lo que cambiás seguido (velocidad). El viejo
 *                           selector de "modo de conversación" (vv/vt/tv/tt)
 *                           MURIÓ: la voz del coach se silencia con el toggle
 *                           de altavoz del header de /conversation.
 *   2. «Tu coach»         — preferencias estables resumidas en chips read-only
 *                           que se expanden inline (variedad, tono, estilo, voz,
 *                           intensidad). «Así está hoy — tocá para ajustar».
 *   3. «Durante la charla»— ayudas y privacidad como toggles INDEPENDIENTES
 *                           (corrección, traducción, charla privada, análisis
 *                           fonético). Corrección y fonético ya NO están atados.
 *   4. «Pantalla»         — display puro (densidad de las burbujas).
 *
 * Mapeo prototipo → datos reales: `variety`→`region`, intensidad/corrección
 * `auto` salen del estado de nivel (`useEnglishLevel`), la sensibilidad de
 * pronunciación es la columna `paAccuracyThreshold` (no `conversationPrefs`).
 */

/**
 * Opciones del selector de sensibilidad de correcciones (Fase 14B). El `id`
 * coincide con `CorrectionLevel` de shared-types; el filtro por severidad vive
 * en `errorModel.filterErrorsByLevel`.
 */
const CORRECTION_LEVEL_OPTIONS: ReadonlyArray<{
  id: ConvSettings['correctionLevel'];
  label: string;
  sub: string;
}> = [
  { id: 'essential', label: 'Lo importante', sub: 'Solo errores que rompen o son claramente gramaticales.' },
  { id: 'balanced', label: 'Equilibrado', sub: 'Errores + deslices menores.' },
  { id: 'all', label: 'Exigente', sub: 'Todo, incluso cómo sonar más natural (sugerencias).' },
];

/**
 * Idioma del reconocimiento de voz (STT). Mapea a `conversationPrefs.sttLanguage`
 * (`'auto' | 'en'`); `null` = por nivel y lo maneja el toggle Auto/Manual. Se
 * fija en el handshake del WS → aplica al iniciar la próxima charla.
 */
// Las dos opciones escuchan inglés Y la lengua nativa (`secondary_languages`
// de Scribe); sólo cambia cuál es el principal. El copy lo dice explícito para
// que «Inglés» no se lea como «sólo inglés».
const STT_LANGUAGE_OPTIONS: ReadonlyArray<{ id: 'auto' | 'en'; label: string; sub: string }> = [
  { id: 'en', label: 'Mayormente inglés', sub: 'Si pasás al español, también te entendemos.' },
  { id: 'auto', label: 'Mayormente español', sub: 'Para cuando todavía hablás más en español, con frases en inglés.' },
];

/**
 * Sensibilidad de pronunciación (Fase 14B): `paAccuracyThreshold` 30-90. Tres
 * presets de UI. Storage numérico (columna tipada, no `conversationPrefs`).
 */
const PA_THRESHOLD_OPTIONS: ReadonlyArray<{ value: number; label: string; sub: string }> = [
  { value: 40, label: 'Permisivo', sub: 'Solo errores de pronunciación fuertes.' },
  { value: 60, label: 'Equilibrado', sub: 'Correcciones balanceadas.' },
  { value: 80, label: 'Estricto', sub: 'Cazá todos los matices fonéticos.' },
];

/** Mapea un threshold numérico al preset más cercano (para marcar el chip activo). */
function paActiveValue(threshold: number): number {
  if (threshold <= 50) return 40;
  if (threshold <= 70) return 60;
  return 80;
}

/** Bandera de cada valor de «Tu inglés» (chip y tarjetas). */
const REGION_FLAG: Record<ConvSettings['region'], string> = {
  american: '🇺🇸',
  british: '🇬🇧',
  australian: '🇦🇺',
};

/** Opción normalizada que consume `CCCards` (label + sub opcional + flag opcional). */
interface CardOption {
  id: string;
  label: string;
  sub?: string;
  flag?: string;
}

function labelOf<T extends { id: string; label: string }>(opts: ReadonlyArray<T>, id: string): string {
  return (opts.find((o) => o.id === id) ?? opts[0]!).label;
}

// ─── Helpers de UI (zonas) ─────────────────────────────────────────────────

type ZoneAccent = 'lime' | 'purple' | 'faint';

/** Cabecera de zona (punto de acento por color + nombre + nota a la derecha). */
function CCZoneHead({ accent = 'lime', name, note }: { accent?: ZoneAccent; name: string; note?: string }) {
  return (
    <div className={`cc-zonehead acc-${accent}`}>
      <span className={`cc-zonehead-dot ${accent === 'faint' ? 'acc-faint' : ''}`} />
      <span className="cc-zonehead-name">{name}</span>
      {note && <span className="cc-zonehead-note">{note}</span>}
    </div>
  );
}

/** Tarjetas opción con label + sub (variedad, tono, niveles de corrección…). */
function CCCards({
  options,
  value,
  onChange,
  cols = 2,
  playingId = null,
  disabled = false,
}: {
  options: ReadonlyArray<CardOption>;
  value: string;
  onChange: (id: string) => void;
  cols?: number;
  /** Opción cuyo demo de audio está sonando (tono) — muestra un ecualizador. */
  playingId?: string | null;
  /** En Auto las opciones sólo muestran la elegida por nivel: se habilitan al pasar a Manual. */
  disabled?: boolean;
}) {
  return (
    // `minmax(0, 1fr)`: con `1fr` a secas cada columna no baja de su palabra más
    // larga (en mono), y a 390 px la tercera tarjeta se salía del panel.
    <div className={`cc-cards cols-${cols}`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            className={`cc-card ${active ? 'is-active' : ''}`}
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            disabled={disabled}
          >
            <span className="cc-card-top">
              {o.flag && <span className="cc-card-flag">{o.flag}</span>}
              <span className="cc-card-label">{o.label}</span>
              {playingId === o.id && (
                <span className="cc-card-eq" aria-label="Sonando">
                  <i />
                  <i />
                  <i />
                </span>
              )}
              {active && <span className="mode-active-dot" />}
            </span>
            {o.sub && <span className="cc-card-sub">{o.sub}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Pills compactas label-only (registro / estilo). */
function CCChips({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: ReadonlyArray<{ id: string; label: string }>;
  value: string;
  onChange: (id: string) => void;
  /** Bloqueado (estilo en starter/cozy): se ve el efectivo, no se toca. */
  disabled?: boolean;
}) {
  return (
    <div className="cc-chiprow">
      {options.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            className={`cc-minichip ${active ? 'is-active' : ''}`}
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            disabled={disabled}
          >
            {active && (
              <span className="cc-minichip-tick">
                <IconCheck size={11} />
              </span>
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Zona 1 «Esta charla» — lo que cambiás seguido ─────────────────────────
function ChatZone({
  settings,
  onChange,
  level,
}: {
  settings: ConvSettings;
  onChange: (next: ConvSettings) => void;
  level: UseEnglishLevel;
}) {
  const setField = <K extends keyof ConvSettings>(k: K, v: ConvSettings[K]) => onChange({ ...settings, [k]: v });
  // Velocidad tri-state (plan 38 F1): null = auto por nivel. La recomendada por
  // nivel EFECTIVO la resuelve el server (`/state.recommendedSpeed`); acá sólo la
  // mostramos y derivamos la efectiva sin duplicar el mapa CEFR→valor.
  const speedAuto = settings.speed === null;
  const recSpeed = level.state?.recommendedSpeed ?? 1;
  const effSpeed = settings.speed ?? recSpeed;
  const levelId = level.state?.cefrGlobal ?? 'B1';
  // Voz del coach silenciada (toggle del header): la velocidad no aplica hasta
  // que la voz vuelva — se muestra atenuada, no se oculta (la pref persiste).
  const coachMuted = coachMutedFromMode(settings.mode);
  return (
    <div className="cc-zone cc-zone-1">
      <CCZoneHead accent="lime" name="Esta charla" note="Lo que cambiás seguido" />

      {/* Velocidad de audio — Auto por nivel / Manual (tri-state, plan 38 F1) */}
      <div className="cc-field" style={coachMuted ? { opacity: 0.55 } : undefined}>
        <div className="cc-subblock-head">
          <span className="cc-field-label">Velocidad de audio</span>
          <AutoManualToggle auto={speedAuto} onToggle={(b) => setField('speed', b ? null : effSpeed)} />
        </div>
        <div className="speed-row cc-speed">
          {SPEECH_RATE_VALUES.map((s) => {
            const active = Math.abs(effSpeed - s) < 0.001;
            return (
              <button
                key={s}
                className={`speed-pill ${active ? 'is-active' : ''}`}
                onClick={() => setField('speed', s)}
                disabled={speedAuto}
              >
                {formatSpeechRateLabel(s)}
              </button>
            );
          })}
        </div>
        <div className={`cc-autonote ${speedAuto ? '' : 'is-manual'}`}>
          {speedAuto ? (
            <>
              <IconWand size={12} />
              <span>
                Auto · por tu nivel <b>{levelId}</b> el coach habla a{' '}
                <b>{formatSpeechRateLabel(effSpeed)}</b>. Pasá a Manual para elegir otra.
              </span>
            </>
          ) : (
            <>
              <IconCheck size={12} />
              <span>Manual · vos elegís a qué velocidad te habla el coach.</span>
            </>
          )}
        </div>
        {coachMuted && (
          <div className="cc-autonote">
            <span>
              La voz del coach está silenciada (altavoz del header) — la
              velocidad aplica cuando vuelva a hablar.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Primera intensidad donde estilo y tono rigen (hoy natural). Derivada del
 * contrato (`isConvStyleLocked`), no hardcodeada: si el corte se mueve, el
 * aviso lo sigue.
 */
const STYLE_UNLOCK_INTENSITY = ENGLISH_INTENSITIES.find((i) => !isConvStyleLocked(i))!;

/**
 * Aviso de estilo/tono bloqueados en starter/cozy: el coach usa el efectivo
 * (neutro/cálido) para no perder el andamiaje en español. La pref guardada NO
 * se pisa: si difiere, se dice que vuelve a regir al subir de nivel.
 */
function StyleLockNote({ effectiveLabel, savedLabel }: { effectiveLabel: string; savedLabel: string }) {
  return (
    <div className="cc-autonote cc-locknote">
      <IconShield size={12} />
      <span>
        Disponible desde el nivel <b>{intensityById(STYLE_UNLOCK_INTENSITY).label}</b>. Por ahora el coach usa{' '}
        <b>{effectiveLabel}</b> para no perder la ayuda en español.
        {savedLabel !== effectiveLabel && (
          <>
            {' '}
            Tu elección (<b>{savedLabel}</b>) vuelve a regir cuando subas.
          </>
        )}
      </span>
    </div>
  );
}

// ─── Zona 2 «Tu coach» — chips read-only que se expanden al tocarlos ───────
function CoachZone({
  settings,
  onChange,
  onIntensityChange,
  coach,
  level,
  initialOpen,
  varietyOnly = false,
  onDone,
}: {
  settings: ConvSettings;
  onChange: (next: ConvSettings) => void;
  onIntensityChange?: ((intensity: ConvSettings['intensity']) => void) | undefined;
  coach: UseCoachPickerReturn;
  level: UseEnglishLevel;
  initialOpen?: 'variety' | undefined;
  /** Sólo «Tu inglés», sin los chips del coach (Perfil de A0). «Listo» cierra la hoja. */
  varietyOnly?: boolean;
  onDone?: () => void;
}) {
  const [open, setOpen] = useState<string | null>(varietyOnly ? 'variety' : (initialOpen ?? null));
  const zoneRef = useRef<HTMLDivElement | null>(null);
  // Entrada directa desde Perfil («Tu inglés»): la zona queda a la vista.
  useEffect(() => {
    if (initialOpen) zoneRef.current?.scrollIntoView({ block: 'start' });
  }, [initialOpen]);
  const setField = <K extends keyof ConvSettings>(k: K, v: ConvSettings[K]) => onChange({ ...settings, [k]: v });

  const activeCoach = coach.coaches.find((c) => c.id === coach.selectedId) ?? null;
  const regionLabel = labelOf(REGION_OPTIONS, settings.region);
  // «Tu inglés» y la voz son UNA elección (encargo 111): cambiar la variedad
  // pone un coach de ese acento si el actual es de otro, y elegir un coach de
  // otro acento cambia la variedad con él. Nunca quedan contradiciéndose.
  const setRegion = (next: ConvSettings['region']): void => {
    setField('region', next);
    if (activeCoach?.accentKey !== CONV_REGION_ACCENT[next]) coach.selectDefaultFor(next);
  };
  const pickCoach = (id: string): void => {
    coach.select(id);
    const picked = coach.coaches.find((c) => c.id === id);
    const next = regionForAccent(picked?.accentKey);
    if (next && next !== settings.region) setField('region', next);
  };
  // "Español del profe": pref explícita ?? la que resuelve el server por país de
  // origen (`/state.recommendedSpanishVariant`). `null` mientras carga /state:
  // no inventamos una variedad en el cliente.
  const spanishVariant: SpanishVariantId | null =
    settings.spanishVariant ?? level.state?.recommendedSpanishVariant ?? null;
  const spanishOpt = SPANISH_VARIANT_OPTIONS.find((o) => o.id === spanishVariant) ?? null;
  const effIntensity = level.state?.effectiveIntensity ?? settings.intensity;
  const intLabel = intensityById(effIntensity).label;
  const isA0Learner = level.state?.cefrGlobal === 'A0';
  // Estilo y tono bloqueados en starter/cozy: se decide con la intensidad con
  // la que el backend corre ESTA sesión (`sessionIntensity`: la del /state bajo
  // overlay de exploración, si no la de esta charla), así lo que se ve
  // bloqueado es lo que se aplica y un cambio en vivo bloquea/desbloquea en el
  // acto.
  const styleIntensity = sessionIntensity(settings.intensity, level.state);
  const styleLocked = isConvStyleLocked(styleIntensity);
  const effStyle = effectiveConvStyle({ ...settings, intensity: styleIntensity });
  const toneLabel = labelOf(TONE_OPTIONS, effStyle.tone);
  const registerLabel = labelOf(REGISTER_OPTIONS, effStyle.register);

  interface Chip {
    key: string;
    keyLabel: string;
    val: string;
    flag?: string;
    dot?: string;
    auto?: boolean;
    locked?: boolean;
  }
  const chips: ReadonlyArray<Chip> = [
    { key: 'variety', keyLabel: 'Tu inglés', val: regionLabel, flag: REGION_FLAG[settings.region] },
    { key: 'tone', keyLabel: 'Tono', val: toneLabel, locked: styleLocked },
    {
      key: 'spanish',
      keyLabel: 'Español del profe',
      val: spanishOpt ? spanishOpt.label.replace(/ \(.*\)$/, '') : '…',
      ...(spanishOpt ? { flag: spanishOpt.flag } : {}),
    },
    { key: 'register', keyLabel: 'Estilo', val: registerLabel, locked: styleLocked },
    ...(activeCoach
      ? [{ key: 'voice', keyLabel: 'Voz', val: activeCoach.name, dot: activeCoach.color } as Chip]
      : [{ key: 'voice', keyLabel: 'Voz', val: 'Elegí tu coach' } as Chip]),
    // An A0 learner always runs at `starter` (the server forces it, plan 48
    // review): no intensity to pick — another one would drop the A0 coach.
    ...(isA0Learner
      ? []
      : [{ key: 'intensity', keyLabel: 'Intensidad', val: intLabel, auto: level.state?.levelAuto ?? false }]),
  ];

  const titles: Record<string, string> = {
    variety: 'Tu inglés',
    tone: 'Tono del coach',
    spanish: 'Español del profe',
    register: 'Estilo y registro',
    voice: 'Tu coach',
    intensity: 'Intensidad del coach',
  };

  return (
    <div className="cc-zone cc-zone-2" ref={zoneRef}>
      {!varietyOnly && (
        <>
          <CCZoneHead accent="purple" name="Tu coach" note="Así está hoy — tocá un chip para ajustar" />
          <div className="cc-coachchips">
            {chips.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`cc-coachchip ${open === c.key ? 'is-open' : ''}`}
                onClick={() => setOpen(open === c.key ? null : c.key)}
                aria-expanded={open === c.key}
              >
                {c.flag && <span className="cc-coachchip-flag">{c.flag}</span>}
                {c.dot && <span className="cc-coachchip-dot" style={{ background: c.dot }} />}
                <span className="cc-coachchip-key">{c.keyLabel}</span>
                <span className="cc-coachchip-val">{c.val}</span>
                {c.auto && (
                  <span className="cc-auto-dot" title="Automático según tu nivel">
                    auto
                  </span>
                )}
                {c.locked && (
                  <span
                    className="cc-auto-dot cc-lock-dot"
                    title={`Disponible desde el nivel ${intensityById(STYLE_UNLOCK_INTENSITY).label}`}
                  >
                    {intensityById(STYLE_UNLOCK_INTENSITY).label}+
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}

      {open && (
        <div className="cc-editor slide-up" key={open}>
          <div className="cc-editor-head">
            <span className="cc-editor-title">{titles[open]}</span>
            <button
              type="button"
              className="cc-editor-done"
              onClick={() => (varietyOnly ? onDone?.() : setOpen(null))}
            >
              Listo
            </button>
          </div>

          {open === 'variety' && (
            <>
              <CCCards
                options={REGION_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                  sub: o.desc,
                  flag: REGION_FLAG[o.id],
                }))}
                value={settings.region}
                onChange={(v) => setRegion(v as ConvSettings['region'])}
                cols={3}
              />
              <div className="cc-autonote">
                <span>
                  Si cambiás, también te ponemos un coach de ese acento
                  {activeCoach ? ` (hoy: ${activeCoach.name})` : ''}.
                </span>
              </div>
            </>
          )}

          {open === 'tone' && (
            <>
              <CCCards
                options={TONE_OPTIONS.map((o) => ({ id: o.id, label: o.label, sub: o.desc }))}
                value={effStyle.tone}
                onChange={(v) => {
                  const tone = v as ConvSettings['tone'];
                  setField('tone', tone);
                  // Demo en el acto con la voz actual: el tap ES el gesto del
                  // user (autoplay permitido). Nunca suena al abrir el editor.
                  coach.playToneDemo(tone);
                }}
                playingId={coach.previewState === 'playing' || coach.previewState === 'loading' ? coach.demoTone : null}
                cols={2}
                disabled={styleLocked}
              />
              {styleLocked ? (
                <StyleLockNote effectiveLabel={toneLabel} savedLabel={labelOf(TONE_OPTIONS, settings.tone)} />
              ) : (
                <div className="cc-autonote">
                  <span>
                    {coach.toneDemoAvailable
                      ? 'Tocá un tono para escucharlo: cambia cómo te trata el coach y cómo suena su voz.'
                      : 'Cambia cómo te trata el coach.'}
                  </span>
                </div>
              )}
            </>
          )}

          {open === 'spanish' && (
            <>
              <CCCards
                options={SPANISH_VARIANT_OPTIONS.map((o) => ({
                  id: o.id,
                  label: o.label,
                  sub: o.desc,
                  flag: o.flag,
                }))}
                value={spanishVariant ?? ''}
                onChange={(v) => {
                  const variant = v as SpanishVariantId;
                  setField('spanishVariant', variant);
                  // Demo en el acto (el tap ES el gesto del user): una línea
                  // bilingüe en esa variedad con la voz y el tono actuales.
                  coach.playSpanishVariantDemo(variant);
                }}
                playingId={
                  coach.previewState === 'playing' || coach.previewState === 'loading'
                    ? coach.demoSpanishVariant
                    : null
                }
                cols={2}
              />
              <div className="cc-autonote">
                <span>Sólo cambia el español de las explicaciones; practicás inglés igual.</span>
              </div>
            </>
          )}

          {open === 'register' && (
            <>
              <CCChips
                options={REGISTER_OPTIONS}
                value={effStyle.register}
                onChange={(v) => setField('register', v as ConvSettings['register'])}
                disabled={styleLocked}
              />
              {styleLocked && (
                <StyleLockNote
                  effectiveLabel={registerLabel}
                  savedLabel={labelOf(REGISTER_OPTIONS, settings.register)}
                />
              )}
            </>
          )}

          {open === 'voice' && (
            <CoachPicker
              coaches={coach.coaches}
              selectedId={coach.selectedId}
              loading={coach.loading}
              error={coach.error}
              previewState={coach.previewState}
              previewingId={coach.previewingId}
              onPick={pickCoach}
              onPreview={coach.preview}
              varietyAccent={CONV_REGION_ACCENT[settings.region]}
              varietyLabel={regionLabel}
            />
          )}

          {open === 'intensity' &&
            !isA0Learner &&
            (level.state?.cefrGlobal != null ? (
              <>
                <IntensityControl
                  levelId={level.state.cefrGlobal}
                  value={settings.intensity}
                  recommended={level.state.recommendedIntensity}
                  auto={level.state.levelAuto}
                  onChange={(id) => {
                    // Elegir intensidad = manual: actualizamos la sesión viva y
                    // persistimos en el nivel ({ intensity, levelAuto:false }).
                    if (onIntensityChange) onIntensityChange(id);
                    else setField('intensity', id);
                    void level.setIntensity(id);
                  }}
                  onToggleAuto={(b) => {
                    const intensity = b ? level.state!.recommendedIntensity : effIntensity;
                    if (onIntensityChange) onIntensityChange(intensity);
                    else setField('intensity', intensity);
                    void level.setLevelAuto(b);
                  }}
                  variant="full"
                />
                {/* El sub-eje de andamiaje A0 (cuánto español de apoyo) ya NO vive
                    acá: dejó de ser una pref de conversación y pasó a la columna
                    `a0_stage` (plan 31 F3), que el motor A0 DERIVA por evidencia
                    (no se elige a mano) y vive en su front separado (plan 32). Un
                    user en estos ajustes es A1+ (cefrGlobal!=='A0'), así que el
                    control no aplica. */}
              </>
            ) : (
              <div className="intensity-desc">
                <span style={{ color: 'var(--text-muted)' }}>Cargando tu nivel…</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// ─── Zona 3 «Durante la charla» — ayudas y privacidad (toggles indep.) ─────
function SessionZone({
  settings,
  onChange,
  level,
  incognito,
  onToggleIncognito,
}: {
  settings: ConvSettings;
  onChange: (next: ConvSettings) => void;
  level: UseEnglishLevel;
  incognito: boolean | undefined;
  onToggleIncognito: (() => void) | undefined;
}) {
  const setField = <K extends keyof ConvSettings>(k: K, v: ConvSettings[K]) => onChange({ ...settings, [k]: v });
  const corrOn = settings.correction;
  const corrAuto = settings.correctionAuto;
  const recCorr = level.state?.recommendedCorrectionLevel ?? 'balanced';
  const corrEff = corrAuto ? recCorr : settings.correctionLevel;
  const levelId = level.state?.cefrGlobal ?? 'B1';
  const phonOn = settings.phonetic;

  return (
    <div className="cc-zone cc-zone-session">
      <CCZoneHead accent="lime" name="Durante la charla" note="Ayudas y privacidad" />

      {/* Corrección de errores — independiente */}
      <div className="settings-toggle-row cc-flat" onClick={() => setField('correction', !corrOn)}>
        <div
          className="settings-toggle-dot"
          style={{
            background: corrOn ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.06)',
            color: corrOn ? '#f87171' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 4 L 20 10 L 9 21 L 3 21 L 3 15 Z" />
            <path d="M13 5 L 19 11" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">Corrección de errores</div>
          <div className="settings-toggle-sub">Marca y explica gramática en tus mensajes</div>
        </div>
        <Switch on={corrOn} />
      </div>

      {/* Sub-selector: nivel de corrección (Auto/Manual) */}
      {corrOn && (
        <div className="cc-subblock">
          <div className="cc-subblock-head">
            <span className="cc-subblock-label">Nivel de corrección</span>
            <AutoManualToggle
              auto={corrAuto}
              onToggle={(b) => onChange({ ...settings, correctionAuto: b, ...(b ? {} : { correctionLevel: corrEff }) })}
            />
          </div>
          <CCCards
            options={CORRECTION_LEVEL_OPTIONS}
            value={corrEff}
            onChange={(id) => onChange({ ...settings, correctionLevel: id as ConvSettings['correctionLevel'], correctionAuto: false })}
            cols={3}
            disabled={corrAuto}
          />
          <div className={`cc-autonote ${corrAuto ? '' : 'is-manual'}`}>
            {corrAuto ? (
              <>
                <IconWand size={12} />
                <span>
                  Auto · por tu nivel <b>{levelId}</b> elegimos <b>{labelOf(CORRECTION_LEVEL_OPTIONS, recCorr)}</b>. Pasá a Manual para elegir otro.
                </span>
              </>
            ) : (
              <>
                <IconCheck size={12} />
                <span>
                  Manual · vos mandás. Recomendado para {levelId}: <b>{labelOf(CORRECTION_LEVEL_OPTIONS, recCorr)}</b>.
                </span>
              </>
            )}
          </div>

          {/* Correcciones por voz (manos libres) — la coda hablada solo dice
              la corrección MÁS GRAVE del turno, después de la respuesta del
              coach. Requiere salida de voz (con salida de texto no suena). */}
          <div
            className="settings-toggle-row cc-flat"
            onClick={() => setField('voiceCorrections', !settings.voiceCorrections)}
          >
            <div
              className="settings-toggle-dot"
              style={{
                background: settings.voiceCorrections ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.06)',
                color: settings.voiceCorrections ? '#f87171' : 'var(--text-faint)',
              }}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 5 L 6 9 H 2 V 15 H 6 L 11 19 Z" />
                <path d="M15.5 8.5 a 5 5 0 0 1 0 7" />
                <path d="M18.5 5.5 a 9.5 9.5 0 0 1 0 13" />
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="settings-toggle-title">Decir correcciones en voz alta</div>
              <div className="settings-toggle-sub">
                Manos libres (auto, gym): después de responder, el coach te dice
                el error más importante. Necesita pantalla encendida y salida de voz.
              </div>
            </div>
            <Switch on={settings.voiceCorrections} />
          </div>
        </div>
      )}

      {/* Traducción en vivo */}
      <div className="settings-toggle-row cc-flat" onClick={() => setField('translation', !settings.translation)}>
        <div
          className="settings-toggle-dot"
          style={{
            background: settings.translation ? 'rgba(167,139,250,0.2)' : 'rgba(255,255,255,0.06)',
            color: settings.translation ? 'var(--purple)' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 5 H 12" />
            <path d="M7 3 V 5" />
            <path d="M5 5 C 5 11, 11 13, 13 13" />
            <path d="M11 5 C 11 9, 7 13, 4 13" />
            <path d="M13 21 L 17.5 11 L 22 21" />
            <path d="M14.5 18 H 20.5" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">Traducción en vivo</div>
          <div className="settings-toggle-sub">Muestra cada frase del coach en español, debajo</div>
        </div>
        <Switch on={settings.translation} />
      </div>

      {/* Búsqueda en internet — costo extra por búsqueda */}
      <div className="settings-toggle-row cc-flat" onClick={() => setField('webSearch', !settings.webSearch)}>
        <div
          className="settings-toggle-dot"
          style={{
            background: settings.webSearch ? 'rgba(96,165,250,0.2)' : 'rgba(255,255,255,0.06)',
            color: settings.webSearch ? '#60a5fa' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12 H 21" />
            <path d="M12 3 a 14 14 0 0 1 0 18 a 14 14 0 0 1 0 -18 z" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">Búsqueda en internet</div>
          <div className="settings-toggle-sub">
            El coach puede buscar cosas actuales en la web. Tiene un costo extra
            por búsqueda — sale más que un mensaje normal.
          </div>
        </div>
        <Switch on={settings.webSearch} />
      </div>

      {/* Charla privada (incógnito) — efímero, no se persiste. Sólo con sesión
          viva (montado standalone desde Perfil no hay charla que marcar). */}
      {onToggleIncognito && (
      <>
      <div className="settings-toggle-row cc-flat" onClick={onToggleIncognito}>
        <div
          className="settings-toggle-dot"
          style={{
            background: incognito ? 'rgba(150,160,190,0.2)' : 'rgba(255,255,255,0.06)',
            color: incognito ? '#c3c9da' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4 L 20 20" />
            <path d="M9.5 5.4 A 9 9 0 0 1 12 5 c 5 0 9 5 9 7 a 11 11 0 0 1 -2 2.6" />
            <path d="M6.2 7.3 C 4 8.8 3 11 3 12 c 0 2, 4 7, 9 7 a 9 9 0 0 0 3 -0.5" />
            <path d="M9.9 9.9 A 3 3 0 0 0 14 14.1" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">Charla privada</div>
          <div className="settings-toggle-sub">No guardo nada: ni en tu memoria, ni en tu historial</div>
        </div>
        <Switch on={!!incognito} />
      </div>
      {incognito && (
        <div className="settings-private-hint">
          Mientras esté activa, vas a ver un cartel arriba en toda la sesión.
        </div>
      )}
      </>
      )}

      {/* Análisis fonético — independiente, experimental y SÓLO web: en el build
          nativo no se ofrece (ni el toggle, ni su sensibilidad, ni el aviso) —
          ver `livePronunciation.ts`. */}
      {LIVE_PA_AVAILABLE && (
      <>
      <div className="settings-toggle-row cc-flat" onClick={() => setField('phonetic', !settings.phonetic)}>
        <div
          className="settings-toggle-dot"
          style={{
            background: phonOn ? 'rgba(251,191,36,0.18)' : 'rgba(255,255,255,0.06)',
            color: phonOn ? '#fbbf24' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 14" width="16" height="9" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
            <path d="M2 7 L 5 7" />
            <path d="M8 2 L 8 12" />
            <path d="M12 4 L 12 10" />
            <path d="M16 1 L 16 13" />
            <path d="M19 7 L 22 7" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">
            Análisis fonético
          </div>
          <div className="settings-toggle-sub">Subraya tu pronunciación mientras hablás</div>
        </div>
        <Switch on={phonOn} />
      </div>

      {/* Sub-selector: sensibilidad de pronunciación — depende de fonética ON */}
      {phonOn && level.state && (
        <div className="cc-subblock">
          <div className="cc-subblock-head">
            <span className="cc-subblock-label">Sensibilidad de pronunciación</span>
          </div>
          <CCCards
            options={PA_THRESHOLD_OPTIONS.map((o) => ({ id: String(o.value), label: o.label, sub: o.sub }))}
            value={String(paActiveValue(level.state.paAccuracyThreshold))}
            onChange={(id) => void level.setPaThreshold(Number(id))}
            cols={3}
          />
        </div>
      )}

      <div className="cc-exp-note">
        <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3 L 22 20 H 2 Z" />
          <path d="M12 10 V 14" />
          <path d="M12 17 H 12.01" />
        </svg>
        <span>
          El análisis de pronunciación es <b>experimental</b>: hoy todavía no es 100% confiable, tomalo como una guía.
        </span>
      </div>
      </>
      )}
    </div>
  );
}

// ─── Zona «Cómo te escuchamos» — reconocimiento de voz (STT) ───────────────
// Ajustes del STT que se fijan en el handshake del WS → se aplican al EMPEZAR
// la próxima charla (no mutan mid-sesión, a diferencia del resto del sheet).
function ListenZone({
  settings,
  onChange,
  level,
}: {
  settings: ConvSettings;
  onChange: (next: ConvSettings) => void;
  level: UseEnglishLevel;
}) {
  const setField = <K extends keyof ConvSettings>(k: K, v: ConvSettings[K]) => onChange({ ...settings, [k]: v });
  const levelId = level.state?.cefrGlobal ?? 'B1';
  const langAuto = settings.sttLanguage === null;
  // Efectivo cuando está en auto (= por nivel): server-resolved (`/state`,
  // preset del plan 38 F1). Antes esta regla (A0 → autodetect, A1+ → inglés)
  // vivía DUPLICADA acá; ahora el server la resuelve y el cliente la consume.
  const effLang: 'auto' | 'en' = settings.sttLanguage ?? level.state?.recommendedSttLanguage ?? 'en';
  const effLangLabel = effLang === 'en' ? 'en inglés' : 'en español';

  return (
    <div className="cc-zone cc-zone-session">
      <CCZoneHead accent="lime" name="Cómo te escuchamos" note="Se aplica en la próxima charla" />

      {/* Idioma del reconocimiento (STT) — Auto por nivel / Manual */}
      <div className="cc-subblock">
        <div className="cc-subblock-head">
          <span className="cc-subblock-label">¿En qué idioma vas a hablar más?</span>
          <AutoManualToggle auto={langAuto} onToggle={(b) => setField('sttLanguage', b ? null : effLang)} />
        </div>
        <CCCards
          options={STT_LANGUAGE_OPTIONS}
          value={effLang}
          onChange={(id) => setField('sttLanguage', id as 'auto' | 'en')}
          cols={2}
          disabled={langAuto}
        />
        <div className={`cc-autonote ${langAuto ? '' : 'is-manual'}`}>
          {langAuto ? (
            <>
              <IconWand size={12} />
              <span>
                Auto · por tu nivel <b>{levelId}</b> asumimos que hablás mayormente <b>{effLangLabel}</b>. Te
                entendemos en inglés y en español igual. Pasá a Manual para elegir otro.
              </span>
            </>
          ) : (
            <>
              <IconCheck size={12} />
              <span>Manual · vos elegís cuál usás más. Te entendemos en inglés y en español igual.</span>
            </>
          )}
        </div>
      </div>

      {/* Limpiar muletillas (no_verbatim) — toggle independiente, default off */}
      <div className="settings-toggle-row cc-flat" onClick={() => setField('cleanTranscript', !settings.cleanTranscript)}>
        <div
          className="settings-toggle-dot"
          style={{
            background: settings.cleanTranscript ? 'rgba(110,231,183,0.18)' : 'rgba(255,255,255,0.06)',
            color: settings.cleanTranscript ? '#6ee7b7' : 'var(--text-faint)',
          }}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 4 L 11 12" />
            <path d="M8 21 C 6 19, 5 16, 6 13 L 12 14 C 13 18, 11 20, 8 21 Z" />
            <path d="M14 9 L 18 13" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="settings-toggle-title">
            Limpiar muletillas
          </div>
          <div className="settings-toggle-sub">
            Saca «eh», «um», dudas y arranques en falso de lo que transcribimos. Por defecto está apagado: se
            transcribe tal cual hablás.
          </div>
        </div>
        <Switch on={settings.cleanTranscript} />
      </div>
    </div>
  );
}

// ─── Zona 4 «Pantalla» — display puro ──────────────────────────────────────
function ScreenZone({ settings, onChange }: { settings: ConvSettings; onChange: (next: ConvSettings) => void }) {
  return (
    <div className="cc-zone cc-zone-3">
      <CCZoneHead accent="faint" name="Pantalla" note="Solo cómo se ve la app" />
      <div className="cc-field">
        <div className="cc-field-label">Tamaño de los mensajes</div>
        <div className="density-row">
          {DENSITY_OPTIONS.map((d) => {
            const active = settings.density === d.id;
            return (
              <button
                key={d.id}
                className={`speed-pill density-pill d-${d.id} ${active ? 'is-active' : ''}`}
                onClick={() => onChange({ ...settings, density: d.id })}
                aria-label={`Tamaño ${d.label}`}
              >
                <span className="density-pill-glyph">
                  <span />
                  <span />
                  <span />
                </span>
                <span className="density-pill-label">{d.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface SettingsSheetProps {
  settings: ConvSettings;
  onChange: (next: ConvSettings) => void;
  /** Elección global deliberada: separada de los cambios ordinarios de sesión. */
  onIntensityChange?: ((intensity: ConvSettings['intensity']) => void) | undefined;
  onClose: () => void;
  /** Estado/handlers del catálogo de voces reales (zona «Tu coach»). */
  coach: UseCoachPickerReturn;
  /**
   * Presentación: bottom-sheet (móvil) o panel lateral derecho (escritorio).
   * El contenido es idéntico — solo cambia el contenedor. Default 'mobile'.
   */
  variant?: 'mobile' | 'desktop';
  /**
   * Charla privada (incógnito) — estado efímero de la SESIÓN, NO parte de
   * `settings`. Opcional: montado standalone (Perfil, sin charla viva) no hay
   * sesión que marcar y la fila no se muestra.
   */
  incognito?: boolean;
  onToggleIncognito?: (() => void) | undefined;
  /** Abre directo el chip «Tu inglés» (entrada desde Perfil). */
  initialOpen?: 'variety' | undefined;
  /**
   * Sólo «Tu inglés» (Perfil de A0, encargo 111): A0 no tiene ajustes de la
   * charla a propósito, pero eligió su inglés en el registro y tiene que poder
   * cambiarlo. Cambiar la variedad sigue arrastrando al coach.
   */
  varietyOnly?: boolean;
}

export function SettingsSheet({
  settings,
  onChange,
  onIntensityChange,
  onClose,
  coach,
  variant = 'mobile',
  incognito,
  onToggleIncognito,
  initialOpen,
  varietyOnly = false,
}: SettingsSheetProps) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const level = useEnglishLevel();
  const isDesktop = variant === 'desktop';
  const { grabProps, sheetStyle } = useSheetDrag(onClose);

  const content = (
    <>
      {/* Header */}
      <div className="settings-head">
        <div>
          <div className="tag" style={{ color: 'var(--lime)' }}>
            {varietyOnly ? 'TU INGLÉS' : 'AJUSTES DE LA CHARLA'}
          </div>
          <div style={{ marginTop: 4 }}>
            <span className="script" style={{ fontSize: 28, lineHeight: 1, color: 'var(--text)' }}>
              {varietyOnly ? 'El inglés que aprendés' : 'Tu coach, a tu medida'}
            </span>
          </div>
        </div>
        <button className="settings-x" onClick={onClose} aria-label="Cerrar">
          <IconXSmall size={14} />
        </button>
      </div>

      {!varietyOnly && <ChatZone settings={settings} onChange={onChange} level={level} />}
      <CoachZone
        settings={settings}
        onChange={onChange}
        onIntensityChange={onIntensityChange}
        coach={coach}
        level={level}
        initialOpen={initialOpen}
        varietyOnly={varietyOnly}
        onDone={onClose}
      />
      {!varietyOnly && (
        <>
          <SessionZone
            settings={settings}
            onChange={onChange}
            level={level}
            incognito={incognito}
            onToggleIncognito={onToggleIncognito}
          />
          <ListenZone settings={settings} onChange={onChange} level={level} />
          <ScreenZone settings={settings} onChange={onChange} />
        </>
      )}

      <div style={{ height: 8 }} />
    </>
  );

  if (isDesktop) {
    return (
      <div className="settings-desktop-backdrop" onClick={onClose}>
        <aside className="settings-desktop-panel" onClick={(e) => e.stopPropagation()}>
          <div className="settings-scroll">{content}</div>
        </aside>
      </div>
    );
  }

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-sheet" style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <div className="settings-handle sheet-grab" {...grabProps} />
        <div className="settings-scroll">{content}</div>
      </div>
    </div>
  );
}
